/**
 * Which status pill tone each domain status gets.
 *
 * Kept here rather than inline in a component so that "incubating is a biological phase,
 * quarantined is critical" is decided once and tested, instead of being re-guessed in
 * every list, card and detail header that renders a status.
 */

export type StatusTone = "neutral" | "positive" | "biological" | "attention" | "critical";

/** Mirrors BreeNova.Domain.Entities.BirdStatus. */
export type BirdStatus =
  | "Unknown"
  | "InAviary"
  | "Quarantined"
  | "Sold"
  | "Loaned"
  | "Escaped"
  | "Deceased";

/** Mirrors BreeNova.Domain.Entities.NestStatus. */
export type NestStatus =
  | "Unknown"
  | "Building"
  | "Laying"
  | "Incubating"
  | "Hatching"
  | "Fledged"
  | "Failed";

/** Mirrors BreeNova.Domain.Entities.EggStatus. */
export type EggStatus =
  | "Laid"
  | "Infertile"
  | "Fertile"
  | "DeadInShell"
  | "Hatched"
  | "Broken"
  | "Removed";

export function birdStatusTone(status: BirdStatus): StatusTone {
  switch (status) {
    case "InAviary":
      return "positive";
    case "Quarantined":
      return "attention";
    case "Escaped":
    case "Deceased":
      return "critical";
    case "Sold":
    case "Loaned":
    case "Unknown":
      return "neutral";
  }
}

/**
 * A bird status as a breeder would write it.
 *
 * The wire carries `InAviary` because that is the enum member's name; a pill that prints
 * it back uppercased reads "INAVIARY", which is not a word. Kept beside the tone so the
 * two facts about a status — how it looks and what it is called — are decided in one
 * place rather than re-spelled in every list and form.
 */
export function birdStatusLabel(status: BirdStatus): string {
  switch (status) {
    case "InAviary":
      return "In aviary";
    case "Quarantined":
      return "Quarantine";
    default:
      return status;
  }
}

export function nestStatusTone(status: NestStatus): StatusTone {
  switch (status) {
    // The active phases of a clutch, in terracotta — the design system reserves that
    // colour for biological markers.
    case "Laying":
    case "Incubating":
    case "Hatching":
      return "biological";
    case "Fledged":
      return "positive";
    case "Failed":
      return "critical";
    case "Building":
    case "Unknown":
      return "neutral";
  }
}

export function eggStatusTone(status: EggStatus): StatusTone {
  switch (status) {
    case "Hatched":
      return "positive";
    case "Fertile":
      return "biological";
    case "Infertile":
    case "DeadInShell":
      return "critical";
    case "Broken":
      return "attention";
    case "Laid":
    case "Removed":
      return "neutral";
  }
}
